import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, MenuItem, TextField, CircularProgress, Typography, Alert
} from '@mui/material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import API_URL from '../../../../config/api';

const statusAluguelOptions = ['Reservado', 'Agendado', 'Ativo', 'Em Andamento', 'Finalizado', 'Cancelado', 'Atrasado', 'Análise'];
const statusPagamentoOptions = ['Pendente', 'Pago'];
const limpezaOptions = ['Lavado', 'Sujo'];

export default function StatusAluguelModal({ open, onClose, aluguel, onUpdate }) {
  const navigate = useNavigate();
  const [statusAluguel, setStatusAluguel] = useState(aluguel.status_aluguel || '');
  const [statusPagamento, setStatusPagamento] = useState(aluguel.status_pagamento || '');
  const [limpeza, setLimpeza] = useState('Lavado');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [vistoriaModalOpen, setVistoriaModalOpen] = useState(false);
  const [vistoriaDecision, setVistoriaDecision] = useState(null);

  // ✅ Atualizar estados quando o aluguel mudar
  useEffect(() => {
    if (aluguel) {
      setStatusAluguel(aluguel.status_aluguel || '');
      setStatusPagamento(aluguel.status_pagamento || '');
      setError('');
    }
  }, [aluguel, open]);

  const handleSave = () => {
    // Se está finalizando, pergunta sobre vistoria antes de salvar
    if (statusAluguel === 'Finalizado' && statusPagamento === 'Pago') {
      setVistoriaDecision(null);
      setVistoriaModalOpen(true);
    } else {
      // Se não está finalizando, salva direto
      salvarStatus();
    }
  };

  const salvarStatus = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/alugueis/${aluguel.id}/status`,
        {
          status_aluguel: statusAluguel,
          status_pagamento: statusPagamento,
          // Só envia limpeza se Finalizado e Pago
          ...(statusAluguel === 'Finalizado' && statusPagamento === 'Pago' ? { limpeza } : {})
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      onUpdate(statusAluguel, statusPagamento, limpeza);
      onClose();
    } catch (err) {
      console.error('Erro ao atualizar status:', err);
      setError('Erro ao atualizar status. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleVistoriaResponse = async (comVistoria) => {
    if (comVistoria) {
      // Salva o status e redireciona para criar vistoria
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        await axios.put(
          `${API_URL}/alugueis/${aluguel.id}/status`,
          {
            status_aluguel: statusAluguel,
            status_pagamento: statusPagamento,
            limpeza
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        onUpdate(statusAluguel, statusPagamento, limpeza);
        setVistoriaModalOpen(false);
        onClose();
        // Redireciona para criar vistoria
        navigate(`/aluguel/${aluguel.id}/vistoria`);
      } catch (err) {
        console.error('Erro ao atualizar status:', err);
        setError('Erro ao atualizar status. Tente novamente.');
        setLoading(false);
      }
    } else {
      // Finaliza sem vistoria
      setVistoriaModalOpen(false);
      await salvarStatus();
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Atualizar Status do Aluguel</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Status do Aluguel"
            value={statusAluguel}
            onChange={e => setStatusAluguel(e.target.value)}
            sx={{ mt: 2 }}
          >
            {statusAluguelOptions.map(status => (
              <MenuItem key={status} value={status}>{status}</MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            label="Status do Pagamento"
            value={statusPagamento}
            onChange={e => setStatusPagamento(e.target.value)}
            sx={{ mt: 2 }}
          >
            {statusPagamentoOptions.map(status => (
              <MenuItem key={status} value={status}>{status}</MenuItem>
            ))}
          </TextField>

          {/* Só mostra campo de limpeza ao finalizar e pagar */}
          {statusAluguel === 'Finalizado' && statusPagamento === 'Pago' && (
            <TextField
              select
              fullWidth
              label="Situação da Limpeza do Veículo"
              value={limpeza}
              onChange={e => setLimpeza(e.target.value)}
              sx={{ mt: 2 }}
            >
              {limpezaOptions.map(opt => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </TextField>
          )}

          {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={loading}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={vistoriaModalOpen} onClose={() => setVistoriaModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adicionar Vistoria de Devolução</DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Deseja adicionar uma vistoria (inspeção) de devolução para este aluguel?
          </Alert>
          <Typography variant="body2" color="textSecondary">
            A vistoria permite registrar o estado do veículo no momento da devolução, incluindo fotos e observações.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleVistoriaResponse(false)}
            disabled={loading}
          >
            Não, Finalizar sem Vistoria
          </Button>
          <Button
            onClick={() => handleVistoriaResponse(true)}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Sim, Adicionar Vistoria'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
